import {
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { Input, Select, Spin } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown'
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation'
import { Trans, useTranslation } from 'react-i18next'
import { IMainState } from 'store/states'
import {
  dc3AttestationsService,
  isDc3AttestationDuplicateNameError,
} from 'api/administration/dc3Attestations'
import {
  DC3_THEMATIC_AREAS,
  formatDc3ThematicAreaLabel,
} from 'config/dc3ThematicAreas'
import RequiredFieldLabel from 'components/reusables/requiredFieldLabel'
import FormErrorMessage from 'components/reusables/formErrorMessage'
import { useNotification } from 'hooks/notifications/useNotification'
import {
  IDc3AttestationDetail,
  IDc3AttestationPayload,
} from 'interfaces/dc3Attestation'
import { Dc3CertificateGenerateModal } from 'components/dc3Certificate'
import UpdateAttestationModal from '../updateAttestationModal'
import Dc4Fields from './Dc4Fields'
import FormUploadImage from './FormUploadImage'
import { isAllowedSignatureFile } from './signatureFileValidation'
import {
  Field,
  FieldControl,
  FormCard,
  FormFooter,
  FormHeader,
  FormModalHost,
  FormRoot,
  HelperText,
  InfoBanner,
  InfoBannerContent,
  InfoBannerIcon,
  InfoBannerText,
  RetroactiveWarningBanner,
  RetroactiveWarningContent,
  RetroactiveWarningIcon,
  RetroactiveWarningText,
  SectionDescription,
  SectionPanel,
  SectionTitle,
  SignerBlock,
  SignerRow,
  SignerTitle,
} from './styled'

export type Dc3AttestationFormMode = 'create' | 'edit'

interface IProps {
  mode: Dc3AttestationFormMode
  attestationId?: number
  onCancel: () => void
  onSaved: (attestation: IDc3AttestationDetail) => void
}

interface FormValues {
  name: string
  companyFiscalName: string
  companyRfc: string
  patronalRegistration: string
  companyActivity: string
  trainingAgentName: string
  trainingAgentRfc: string
  trainingAgentStpsRegistration: string
  thematicAreaCode: string
  dc4TrainingAgentType: number | null
  dc4TrainingModality: number | null
  dc4TrainingObjective: number | null
  instructorName: string
  instructorSignature: File | string | null
  legalRepresentativeName: string
  legalRepresentativeSignature: File | string | null
  workersRepresentativeName: string
  workersRepresentativeSignature: File | string | null
}

type FormErrors = Partial<Record<keyof FormValues, string>>

const EMPTY_FORM_VALUES: FormValues = {
  name: '',
  companyFiscalName: '',
  companyRfc: '',
  patronalRegistration: '',
  companyActivity: '',
  trainingAgentName: '',
  trainingAgentRfc: '',
  trainingAgentStpsRegistration: '',
  thematicAreaCode: '',
  dc4TrainingAgentType: null,
  dc4TrainingModality: null,
  dc4TrainingObjective: null,
  instructorName: '',
  instructorSignature: null,
  legalRepresentativeName: '',
  legalRepresentativeSignature: null,
  workersRepresentativeName: '',
  workersRepresentativeSignature: null,
}

const TEXT_FIELDS: Array<keyof FormValues> = [
  'name',
  'companyFiscalName',
  'companyRfc',
  'patronalRegistration',
  'companyActivity',
  'trainingAgentName',
  'trainingAgentStpsRegistration',
  'thematicAreaCode',
  'instructorName',
  'legalRepresentativeName',
  'workersRepresentativeName',
]

const DC4_TEXT_FIELDS: Array<keyof FormValues> = ['trainingAgentRfc']

const DC4_SELECT_FIELDS: Array<keyof FormValues> = [
  'dc4TrainingAgentType',
  'dc4TrainingModality',
  'dc4TrainingObjective',
]

const SIGNATURE_FIELDS: Array<keyof FormValues> = [
  'instructorSignature',
  'legalRepresentativeSignature',
  'workersRepresentativeSignature',
]

const VALIDATION_I18N_KEYS: Record<keyof FormValues, string> = {
  name: 'name',
  companyFiscalName: 'companyFiscalName',
  companyRfc: 'companyRfc',
  patronalRegistration: 'patronalRegistration',
  companyActivity: 'companyActivity',
  trainingAgentName: 'trainingAgentName',
  trainingAgentRfc: 'trainingAgentRfc',
  trainingAgentStpsRegistration: 'trainingAgentStpsRegistration',
  thematicAreaCode: 'thematicArea',
  dc4TrainingAgentType: 'dc4TrainingAgentType',
  dc4TrainingModality: 'dc4TrainingModality',
  dc4TrainingObjective: 'dc4TrainingObjective',
  instructorName: 'instructorName',
  instructorSignature: 'instructorSignature',
  legalRepresentativeName: 'legalRepresentativeName',
  legalRepresentativeSignature: 'legalRepresentativeSignature',
  workersRepresentativeName: 'workersRepresentativeName',
  workersRepresentativeSignature: 'workersRepresentativeSignature',
}

const SECTION_KEYS = ['company', 'worker', 'training', 'signers'] as const

type SectionKey = typeof SECTION_KEYS[number]

interface FieldBlockProps {
  label: string
  error?: string
  helper?: ReactNode
  children: ReactNode
  halfWidth?: boolean
}

const FieldBlock = ({
  label,
  error,
  helper,
  children,
  halfWidth,
}: FieldBlockProps) => (
  <Field $halfWidth={halfWidth}>
    <RequiredFieldLabel required>{label}</RequiredFieldLabel>
    <FieldControl $hasError={!!error}>{children}</FieldControl>
    {!error && helper}
    <FormErrorMessage message={error} />
  </Field>
)

const Dc3AttestationForm = ({
  mode,
  attestationId,
  onCancel,
  onSaved,
}: IProps) => {
  const { t } = useTranslation()
  const notification = useNotification()
  const dc4Enabled = useSelector(
    ({ general }: IMainState) => general.permissions.dc4Enabled
  )
  const prefix = 'administration.certificate.legalAttestationForm'
  const [values, setValues] = useState<FormValues>(EMPTY_FORM_VALUES)
  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(mode === 'edit')
  const [saving, setSaving] = useState(false)
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false)
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(
    {
      company: true,
      worker: true,
      training: true,
      signers: true,
    }
  )

  const getFieldValidationMessage = useCallback(
    (field: keyof FormValues) =>
      t(`${prefix}.validation.${VALIDATION_I18N_KEYS[field]}`),
    [t, prefix]
  )

  const title = useMemo(
    () =>
      mode === 'create'
        ? t(`${prefix}.title.create`)
        : t(`${prefix}.title.edit`),
    [mode, t, prefix]
  )

  const updateField = <K extends keyof FormValues>(
    key: K,
    value: FormValues[K]
  ) => {
    setValues((current) => ({ ...current, [key]: value }))
    setErrors((current) => {
      if (!current[key]) return current
      const next = { ...current }
      delete next[key]
      return next
    })
  }

  const validateValues = (formValues: FormValues): FormErrors => {
    const nextErrors: FormErrors = {}

    TEXT_FIELDS.forEach((field) => {
      const value = formValues[field]
      if (typeof value !== 'string' || !value.trim()) {
        nextErrors[field] = getFieldValidationMessage(field)
      }
    })

    if (dc4Enabled) {
      DC4_TEXT_FIELDS.forEach((field) => {
        const value = formValues[field]
        if (typeof value !== 'string' || !value.trim()) {
          nextErrors[field] = getFieldValidationMessage(field)
        }
      })

      DC4_SELECT_FIELDS.forEach((field) => {
        if (formValues[field] == null) {
          nextErrors[field] = getFieldValidationMessage(field)
        }
      })
    }

    SIGNATURE_FIELDS.forEach((field) => {
      const value = formValues[field]
      if (!(value instanceof File || (typeof value === 'string' && value))) {
        nextErrors[field] = getFieldValidationMessage(field)
      } else if (value instanceof File && !isAllowedSignatureFile(value)) {
        nextErrors[field] = t(`${prefix}.fields.signature.invalidFormat`)
      }
    })

    return nextErrors
  }

  const loadAttestation = useCallback(async () => {
    if (mode !== 'edit' || !attestationId) return

    setLoading(true)
    try {
      const detail = await dc3AttestationsService.getDc3Attestation(
        attestationId
      )
      setValues({
        name: detail.name,
        companyFiscalName: detail.companyFiscalName,
        companyRfc: detail.companyRfc,
        patronalRegistration: detail.patronalRegistration,
        companyActivity: detail.companyActivity,
        trainingAgentName: detail.trainingAgentName,
        trainingAgentRfc: detail.trainingAgentRfc || '',
        trainingAgentStpsRegistration: detail.trainingAgentStpsRegistration,
        thematicAreaCode: detail.thematicArea?.code || '',
        dc4TrainingAgentType: detail.dc4TrainingAgentType ?? null,
        dc4TrainingModality: detail.dc4TrainingModality ?? null,
        dc4TrainingObjective: detail.dc4TrainingObjective ?? null,
        instructorName: detail.instructorName,
        instructorSignature: detail.instructorSignatureUrl,
        legalRepresentativeName: detail.legalRepresentativeName,
        legalRepresentativeSignature: detail.legalRepresentativeSignatureUrl,
        workersRepresentativeName: detail.workersRepresentativeName,
        workersRepresentativeSignature:
          detail.workersRepresentativeSignatureUrl,
      })
      setErrors({})
    } finally {
      setLoading(false)
    }
  }, [attestationId, mode])

  useEffect(() => {
    loadAttestation()
  }, [loadAttestation])

  const toggleSection = (key: SectionKey) => {
    setOpenSections((current) => ({ ...current, [key]: !current[key] }))
  }

  const buildPayload = (formValues: FormValues): IDc3AttestationPayload => {
    const thematicArea = DC3_THEMATIC_AREAS.find(
      (item) => item.code === String(formValues.thematicAreaCode)
    )

    const payload: IDc3AttestationPayload = {
      name: formValues.name.trim(),
      companyFiscalName: formValues.companyFiscalName.trim(),
      companyRfc: formValues.companyRfc.trim(),
      patronalRegistration: formValues.patronalRegistration.trim(),
      companyActivity: formValues.companyActivity.trim(),
      trainingAgentName: formValues.trainingAgentName.trim(),
      trainingAgentStpsRegistration:
        formValues.trainingAgentStpsRegistration.trim(),
      thematicArea: thematicArea || {
        code: formValues.thematicAreaCode,
        name: '',
      },
      instructorName: formValues.instructorName.trim(),
      instructorSignature:
        formValues.instructorSignature instanceof File
          ? formValues.instructorSignature
          : undefined,
      legalRepresentativeName: formValues.legalRepresentativeName.trim(),
      legalRepresentativeSignature:
        formValues.legalRepresentativeSignature instanceof File
          ? formValues.legalRepresentativeSignature
          : undefined,
      workersRepresentativeName: formValues.workersRepresentativeName.trim(),
      workersRepresentativeSignature:
        formValues.workersRepresentativeSignature instanceof File
          ? formValues.workersRepresentativeSignature
          : undefined,
    }

    if (dc4Enabled) {
      payload.trainingAgentRfc = formValues.trainingAgentRfc.trim()
      payload.dc4TrainingAgentType = formValues.dc4TrainingAgentType
      payload.dc4TrainingModality = formValues.dc4TrainingModality
      payload.dc4TrainingObjective = formValues.dc4TrainingObjective
    }

    return payload
  }

  const persistAttestation = async (formValues: FormValues) => {
    setSaving(true)
    try {
      const payload = buildPayload(formValues)
      let savedAttestation: IDc3AttestationDetail

      if (mode === 'create') {
        savedAttestation = await dc3AttestationsService.createDc3Attestation(
          payload
        )
      } else if (attestationId) {
        savedAttestation = await dc3AttestationsService.updateDc3Attestation(
          attestationId,
          payload
        )
      } else {
        return
      }

      notification.successWithDescription(
        t(`${prefix}.notifications.saveSuccessTitle`),
        t(`${prefix}.notifications.saveSuccessDescription`)
      )
      onSaved(savedAttestation)
    } catch (error) {
      if (isDc3AttestationDuplicateNameError(error)) {
        notification.warning(t(`${prefix}.notifications.duplicateNameWarning`))
        return
      }

      notification.error(
        t(
          mode === 'create'
            ? `${prefix}.notifications.createError`
            : `${prefix}.notifications.updateError`
        )
      )
    } finally {
      setSaving(false)
      setConfirmUpdateOpen(false)
      setPendingValues(null)
    }
  }

  const handleSubmit = async (formValues: FormValues) => {
    if (mode === 'edit') {
      setPendingValues(formValues)
      setConfirmUpdateOpen(true)
      return
    }

    await persistAttestation(formValues)
  }

  const handleConfirmUpdate = async () => {
    if (!pendingValues) return
    await persistAttestation(pendingValues)
  }

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextErrors = validateValues(values)
    setErrors(nextErrors)

    if (Object.keys(nextErrors).length > 0) return
    handleSubmit(values)
  }

  const previewAttestation = useMemo(() => {
    const selectedArea = DC3_THEMATIC_AREAS.find(
      (item) => item.code === String(values.thematicAreaCode)
    )

    return {
      companyFiscalName: values.companyFiscalName,
      companyRfc: values.companyRfc,
      patronalRegistration: values.patronalRegistration,
      companyActivity: values.companyActivity,
      trainingAgentName: values.trainingAgentName,
      trainingAgentStpsRegistration: values.trainingAgentStpsRegistration,
      thematicArea:
        selectedArea ||
        (values.thematicAreaCode
          ? { code: values.thematicAreaCode, name: '' }
          : undefined),
      instructorName: values.instructorName,
      legalRepresentativeName: values.legalRepresentativeName,
      workersRepresentativeName: values.workersRepresentativeName,
    }
  }, [values])

  const handlePreview = () => {
    setPreviewModalOpen(true)
  }

  const renderSectionHeader = (key: SectionKey, label: string) => (
    <SectionTitle type="button" onClick={() => toggleSection(key)}>
      <FontAwesomeIcon icon={openSections[key] ? faChevronUp : faChevronDown} />
      {label}
    </SectionTitle>
  )

  return (
    <FormCard id="dc3-attestation-form">
      <FormHeader>
        <button
          type="button"
          onClick={onCancel}
          aria-label={t(`${prefix}.actions.cancel`)}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>{title}</h2>
      </FormHeader>

      <Spin spinning={loading || saving}>
        <FormRoot onSubmit={handleFormSubmit}>
          {mode === 'edit' && (
            <RetroactiveWarningBanner>
              <RetroactiveWarningIcon>
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </RetroactiveWarningIcon>
              <RetroactiveWarningContent>
                <RetroactiveWarningText>
                  <Trans
                    i18nKey={`${prefix}.retroactiveWarning`}
                    components={{ strong: <strong /> }}
                  />
                </RetroactiveWarningText>
              </RetroactiveWarningContent>
            </RetroactiveWarningBanner>
          )}

          <FieldBlock
            label={t(`${prefix}.fields.name.label`)}
            error={errors.name}
            halfWidth
            helper={
              <HelperText>
                <FontAwesomeIcon icon={faInfoCircle} />
                {t(`${prefix}.fields.name.helper`)}
              </HelperText>
            }
          >
            <Input
              value={values.name}
              onChange={(event) => updateField('name', event.target.value)}
            />
          </FieldBlock>

          <SectionPanel>
            {renderSectionHeader(
              'company',
              t(`${prefix}.sections.company.title`)
            )}
            {openSections.company && (
              <>
                <FieldBlock
                  label={t(`${prefix}.fields.companyFiscalName.label`)}
                  error={errors.companyFiscalName}
                >
                  <Input
                    value={values.companyFiscalName}
                    onChange={(event) =>
                      updateField('companyFiscalName', event.target.value)
                    }
                  />
                </FieldBlock>
                <FieldBlock
                  label={t(`${prefix}.fields.companyRfc.label`)}
                  error={errors.companyRfc}
                >
                  <Input
                    value={values.companyRfc}
                    onChange={(event) =>
                      updateField('companyRfc', event.target.value)
                    }
                  />
                </FieldBlock>
                <FieldBlock
                  label={t(`${prefix}.fields.patronalRegistration.label`)}
                  error={errors.patronalRegistration}
                >
                  <Input
                    value={values.patronalRegistration}
                    onChange={(event) =>
                      updateField('patronalRegistration', event.target.value)
                    }
                  />
                </FieldBlock>
                <FieldBlock
                  label={t(`${prefix}.fields.companyActivity.label`)}
                  error={errors.companyActivity}
                >
                  <Input
                    value={values.companyActivity}
                    onChange={(event) =>
                      updateField('companyActivity', event.target.value)
                    }
                  />
                </FieldBlock>
              </>
            )}
          </SectionPanel>

          <SectionPanel>
            {renderSectionHeader(
              'worker',
              t(`${prefix}.sections.worker.title`)
            )}
            {openSections.worker && (
              <InfoBanner>
                <InfoBannerIcon>
                  <FontAwesomeIcon icon={faInfoCircle} />
                </InfoBannerIcon>
                <InfoBannerContent>
                  <InfoBannerText>
                    <Trans
                      i18nKey={`${prefix}.sections.worker.banner`}
                      components={{ strong: <strong /> }}
                    />
                  </InfoBannerText>
                </InfoBannerContent>
              </InfoBanner>
            )}
          </SectionPanel>

          <SectionPanel>
            {renderSectionHeader(
              'training',
              t(`${prefix}.sections.training.title`)
            )}
            {openSections.training && (
              <>
                <InfoBanner>
                  <InfoBannerIcon>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </InfoBannerIcon>
                  <InfoBannerContent>
                    <InfoBannerText>
                      <Trans
                        i18nKey={`${prefix}.sections.training.banner`}
                        components={{ strong: <strong /> }}
                      />
                    </InfoBannerText>
                  </InfoBannerContent>
                </InfoBanner>
                <FieldBlock
                  label={t(`${prefix}.fields.trainingAgentName.label`)}
                  error={errors.trainingAgentName}
                >
                  <Input
                    value={values.trainingAgentName}
                    onChange={(event) =>
                      updateField('trainingAgentName', event.target.value)
                    }
                  />
                </FieldBlock>
                <FieldBlock
                  label={t(
                    `${prefix}.fields.trainingAgentStpsRegistration.label`
                  )}
                  error={errors.trainingAgentStpsRegistration}
                >
                  <Input
                    value={values.trainingAgentStpsRegistration}
                    onChange={(event) =>
                      updateField(
                        'trainingAgentStpsRegistration',
                        event.target.value
                      )
                    }
                  />
                </FieldBlock>
                <FieldBlock
                  label={t(`${prefix}.fields.thematicArea.label`)}
                  error={errors.thematicAreaCode}
                  helper={
                    <HelperText>
                      <FontAwesomeIcon icon={faInfoCircle} />
                      {t(`${prefix}.fields.thematicArea.helper`)}
                    </HelperText>
                  }
                >
                  <Select
                    value={values.thematicAreaCode || undefined}
                    placeholder={t(`${prefix}.fields.thematicArea.placeholder`)}
                    onChange={(value: string) =>
                      updateField('thematicAreaCode', value)
                    }
                  >
                    {DC3_THEMATIC_AREAS.map((area) => (
                      <Select.Option key={area.code} value={area.code}>
                        {formatDc3ThematicAreaLabel(area)}
                      </Select.Option>
                    ))}
                  </Select>
                </FieldBlock>
                {dc4Enabled ? (
                  <Dc4Fields
                    values={{
                      trainingAgentRfc: values.trainingAgentRfc,
                      dc4TrainingAgentType: values.dc4TrainingAgentType,
                      dc4TrainingModality: values.dc4TrainingModality,
                      dc4TrainingObjective: values.dc4TrainingObjective,
                    }}
                    errors={{
                      trainingAgentRfc: errors.trainingAgentRfc,
                      dc4TrainingAgentType: errors.dc4TrainingAgentType,
                      dc4TrainingModality: errors.dc4TrainingModality,
                      dc4TrainingObjective: errors.dc4TrainingObjective,
                    }}
                    onChange={(field, value) => updateField(field, value)}
                  />
                ) : null}
              </>
            )}
          </SectionPanel>

          <SectionPanel $hideBorder>
            {renderSectionHeader(
              'signers',
              t(`${prefix}.sections.signers.title`)
            )}
            {openSections.signers && (
              <>
                <SectionDescription>
                  {t(`${prefix}.sections.signers.description`)}
                </SectionDescription>

                <SignerBlock>
                  <SignerTitle>
                    {t(`${prefix}.sections.signers.instructor.title`)}
                  </SignerTitle>
                  <SignerRow>
                    <FieldBlock
                      label={t(`${prefix}.fields.signerName.label`)}
                      error={errors.instructorName}
                    >
                      <Input
                        value={values.instructorName}
                        onChange={(event) =>
                          updateField('instructorName', event.target.value)
                        }
                      />
                    </FieldBlock>
                    <FieldBlock
                      label={t(`${prefix}.fields.signature.label`)}
                      error={errors.instructorSignature}
                    >
                      <FormUploadImage
                        value={values.instructorSignature}
                        hasError={!!errors.instructorSignature}
                        onChange={(file) =>
                          updateField('instructorSignature', file)
                        }
                      />
                    </FieldBlock>
                  </SignerRow>
                </SignerBlock>

                <SignerBlock>
                  <SignerTitle>
                    {t(`${prefix}.sections.signers.legal.title`)}
                  </SignerTitle>
                  <SignerRow>
                    <FieldBlock
                      label={t(`${prefix}.fields.signerName.label`)}
                      error={errors.legalRepresentativeName}
                    >
                      <Input
                        value={values.legalRepresentativeName}
                        onChange={(event) =>
                          updateField(
                            'legalRepresentativeName',
                            event.target.value
                          )
                        }
                      />
                    </FieldBlock>
                    <FieldBlock
                      label={t(`${prefix}.fields.signature.label`)}
                      error={errors.legalRepresentativeSignature}
                    >
                      <FormUploadImage
                        value={values.legalRepresentativeSignature}
                        hasError={!!errors.legalRepresentativeSignature}
                        onChange={(file) =>
                          updateField('legalRepresentativeSignature', file)
                        }
                      />
                    </FieldBlock>
                  </SignerRow>
                </SignerBlock>

                <SignerBlock>
                  <SignerTitle>
                    {t(`${prefix}.sections.signers.workers.title`)}
                  </SignerTitle>
                  <SignerRow>
                    <FieldBlock
                      label={t(`${prefix}.fields.signerName.label`)}
                      error={errors.workersRepresentativeName}
                    >
                      <Input
                        value={values.workersRepresentativeName}
                        onChange={(event) =>
                          updateField(
                            'workersRepresentativeName',
                            event.target.value
                          )
                        }
                      />
                    </FieldBlock>
                    <FieldBlock
                      label={t(`${prefix}.fields.signature.label`)}
                      error={errors.workersRepresentativeSignature}
                    >
                      <FormUploadImage
                        value={values.workersRepresentativeSignature}
                        hasError={!!errors.workersRepresentativeSignature}
                        onChange={(file) =>
                          updateField('workersRepresentativeSignature', file)
                        }
                      />
                    </FieldBlock>
                  </SignerRow>
                </SignerBlock>
              </>
            )}
          </SectionPanel>

          <FormFooter>
            <button type="button" className="preview" onClick={handlePreview}>
              {t(`${prefix}.actions.preview`)}
            </button>
            <div className="actions">
              <button type="button" className="cancel" onClick={onCancel}>
                {t(`${prefix}.actions.cancel`)}
              </button>
              <button type="submit" className="save" disabled={saving}>
                {t(`${prefix}.actions.saveChanges`)}
              </button>
            </div>
          </FormFooter>
        </FormRoot>
      </Spin>

      <FormModalHost>
        <UpdateAttestationModal
          isOpen={confirmUpdateOpen}
          processing={saving}
          onClose={() => {
            if (saving) return
            setConfirmUpdateOpen(false)
            setPendingValues(null)
          }}
          onConfirm={handleConfirmUpdate}
        />
        <Dc3CertificateGenerateModal
          open={previewModalOpen}
          mode="preview"
          previewConfig={{
            attestationId,
            attestation: previewAttestation,
            signatureOverrides: {
              instructorSignature: values.instructorSignature,
              legalRepresentativeSignature: values.legalRepresentativeSignature,
              workersRepresentativeSignature:
                values.workersRepresentativeSignature,
            },
          }}
          onClose={() => setPreviewModalOpen(false)}
        />
      </FormModalHost>
    </FormCard>
  )
}

export default Dc3AttestationForm
